import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, LogOut } from "lucide-react";
import { toast } from "sonner";
import {
  adminFetchOrders,
  adminFetchRequests,
  adminLogin,
  adminLogout,
  adminMe,
  adminPatchOrder,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const money = (cents) => `$${(cents / 100).toLocaleString()}`;
const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-fog/40 outline-none transition-colors duration-300 focus:border-white/60";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [orders, setOrders] = useState([]);
  const [labels, setLabels] = useState({});
  const [requests, setRequests] = useState({ cancellations: [], buyouts: [] });
  const [tab, setTab] = useState("orders");
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    const [o, r] = await Promise.all([adminFetchOrders(), adminFetchRequests()]);
    setOrders(o.data.orders);
    setLabels(o.data.fulfillment_labels);
    setRequests(r.data);
  }, []);

  useEffect(() => {
    adminMe()
      .then(({ data }) => {
        setUser(data);
        load().catch(() => {});
      })
      .catch(() => setUser(false));
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await adminLogin(loginForm);
      setUser(data);
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Login failed");
    }
  };

  const logout = async () => {
    await adminLogout().catch(() => {});
    setUser(false);
  };

  const saveOrder = async (order) => {
    const draft = drafts[order.id] || {};
    try {
      const { data } = await adminPatchOrder(order.id, {
        fulfillment_status: draft.fulfillment_status ?? order.fulfillment_status,
        internal_notes: draft.internal_notes ?? order.internal_notes,
      });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      toast.success(`Order ${order.order_number} updated`);
    } catch {
      toast.error("Couldn't update the order");
    }
  };

  if (user === null) {
    return <div className="min-h-screen bg-obsidian" />;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-obsidian text-white">
        <div className="mx-auto max-w-md px-5 pt-40">
          <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog">Findley St</p>
          <h1 className="mt-4 font-display text-4xl font-medium tracking-[-0.02em]">Admin</h1>
          <form onSubmit={login} className="mt-10 space-y-5" data-testid="admin-login-form">
            <input type="email" required placeholder="Email" aria-label="Email" data-testid="admin-email"
              className={inputCls} value={loginForm.email}
              onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} />
            <input type="password" required placeholder="Password" aria-label="Password" data-testid="admin-password"
              className={inputCls} value={loginForm.password}
              onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} />
            {loginError && <p className="text-sm text-red-400" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" data-testid="admin-login-submit"
              className="group flex w-full items-center justify-center gap-2 border border-white bg-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-obsidian transition-colors duration-500 hover:bg-transparent hover:text-white">
              Sign in <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5" />
            </button>
          </form>
        </div>
      </div>
    );
  }

  const needsAction = (o) => o.payment_status === "paid" && o.fulfillment_status !== "completed";

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Seo title="Orders" noindex path="/admin" />
      <div className="mx-auto max-w-5xl px-5 pb-32 pt-28 md:px-10 md:pt-36">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog">Findley St</p>
            <h1 className="mt-4 font-display text-4xl font-medium tracking-[-0.02em] md:text-5xl">Orders</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors hover:text-white">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        <div className="mt-10 flex gap-6 border-b border-white/10">
          {["orders", "requests"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`pb-4 text-xs uppercase tracking-[0.3em] transition-colors ${tab === t ? "text-white" : "text-fog/50 hover:text-fog"}`}>
              {t === "orders" ? `Orders (${orders.length})` : `Requests (${requests.cancellations.length + requests.buyouts.length})`}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-8 space-y-4" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="text-sm text-fog">No orders yet.</p>}
            {orders.map((o) => (
              <motion.div key={o.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
                className="border border-white/10" data-testid={`admin-order-${o.order_number}`}>
                <button type="button" onClick={() => setOpenId(openId === o.id ? null : o.id)}
                  className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4 text-left">
                  <span className="font-display text-lg font-medium">{o.order_number}</span>
                  <span className="text-sm text-fog">{o.customer.business_name}</span>
                  <span className="text-sm text-fog">{o.product_name}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-fog/70">{o.purchase_type === "own" ? "Own It" : "Monthly"}</span>
                  <span className="text-sm">{money(o.amount_cents)}{o.purchase_type === "waas" ? "/mo" : ""}</span>
                  <span className={`border px-2 py-1 text-[10px] uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "border-white/40 text-white" : "border-white/10 text-fog/60"}`}>
                    {o.payment_status}{o.subscription_status && o.subscription_status !== "active" ? ` · ${o.subscription_status}` : ""}
                  </span>
                  {needsAction(o) && <span className="h-2 w-2 rounded-full bg-white" aria-label="Needs action" />}
                  <span className="ml-auto text-xs text-fog/60">{labels[o.fulfillment_status] || o.fulfillment_status}</span>
                </button>
                {openId === o.id && (
                  <div className="border-t border-white/10 px-5 py-6" data-testid={`admin-order-detail-${o.order_number}`}>
                    <div className="grid gap-x-10 gap-y-3 text-sm md:grid-cols-2">
                      <p><span className="text-fog/60">Customer:</span> {o.customer.name}</p>
                      <p><span className="text-fog/60">Email:</span> {o.customer.email}</p>
                      <p><span className="text-fog/60">Phone:</span> {o.customer.phone || "—"}</p>
                      <p><span className="text-fog/60">Existing site:</span> {o.customer.existing_website || "—"}</p>
                      <p><span className="text-fog/60">Services:</span> {o.customer.services || "—"}</p>
                      <p><span className="text-fog/60">Service area:</span> {o.customer.service_area || "—"}</p>
                      <p className="md:col-span-2"><span className="text-fog/60">Requested changes:</span> {o.customer.changes || "—"}</p>
                      <p className="md:col-span-2"><span className="text-fog/60">Additional:</span> {o.customer.additional || "—"}</p>
                      {o.assets?.length > 0 && (
                        <p className="md:col-span-2">
                          <span className="text-fog/60">Files:</span>{" "}
                          {o.assets.map((a) => (
                            <a key={a.path} href={`/api/files/${a.path}`} target="_blank" rel="noopener noreferrer"
                              className="mr-3 underline underline-offset-4">{a.filename}</a>
                          ))}
                        </p>
                      )}
                      <p><span className="text-fog/60">Ordered:</span> {fmtDate(o.created_at)}</p>
                      <p><span className="text-fog/60">Paid:</span> {fmtDate(o.paid_at)}</p>
                    </div>
                    <div className="mt-6 flex flex-wrap items-end gap-4 border-t border-white/10 pt-6">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-fog/70">Fulfillment</span>
                        <select
                          data-testid={`fulfillment-select-${o.order_number}`}
                          className="mt-2 block border border-white/15 bg-obsidian px-4 py-2.5 text-sm outline-none"
                          value={(drafts[o.id]?.fulfillment_status) ?? o.fulfillment_status}
                          onChange={(e) => setDrafts((d) => ({ ...d, [o.id]: { ...d[o.id], fulfillment_status: e.target.value } }))}
                        >
                          {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </label>
                      <label className="block flex-1 min-w-[220px]">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-fog/70">Internal notes</span>
                        <input
                          data-testid={`notes-input-${o.order_number}`}
                          className="mt-2 w-full border border-white/15 bg-transparent px-4 py-2.5 text-sm outline-none focus:border-white/60"
                          value={(drafts[o.id]?.internal_notes) ?? o.internal_notes ?? ""}
                          onChange={(e) => setDrafts((d) => ({ ...d, [o.id]: { ...d[o.id], internal_notes: e.target.value } }))}
                        />
                      </label>
                      <button onClick={() => saveOrder(o)} data-testid={`save-order-${o.order_number}`}
                        className="border border-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-white hover:text-obsidian">
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="mt-8 space-y-8" data-testid="admin-requests-list">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-fog">Cancellation requests</p>
              {requests.cancellations.length === 0 && <p className="mt-3 text-sm text-fog/60">None.</p>}
              {requests.cancellations.map((r) => (
                <div key={r.id} className="mt-3 border border-white/10 px-5 py-4 text-sm" data-testid={`admin-cancel-${r.order_number}`}>
                  <p className="font-medium">{r.order_number} — {r.product_name}</p>
                  <p className="mt-1 text-fog">{r.email} · requested {fmtDate(r.requested_at)}</p>
                  <p className="mt-1 text-fog">
                    Next billing {fmtDate(r.next_billing_date)} · effective {fmtDate(r.effective_cancellation_date)} ·{" "}
                    {r.already_scheduled ? "already scheduled in Stripe" : r.notice_met ? "30-day notice met" : "inside notice — next invoice still charges"}
                  </p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-fog">Buyout requests</p>
              {requests.buyouts.length === 0 && <p className="mt-3 text-sm text-fog/60">None.</p>}
              {requests.buyouts.map((r) => (
                <div key={r.id} className="mt-3 border border-white/10 px-5 py-4 text-sm" data-testid={`admin-buyout-${r.order_number}`}>
                  <p className="font-medium">{r.order_number} — {r.product_name}</p>
                  <p className="mt-1 text-fog">{r.email} · requested {fmtDate(r.requested_at)} · buyout {money(r.buyout_price_cents)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
